import { useCallback } from "react";
import { useApi } from "../api";
import type { ReportRequestBody } from "./types/report";

const getConcreteResinReportUrl = (calculationId: string): string => `/modules/concrete_resin/${calculationId}/report`;

const DEFAULT_REPORT_MODULE_NAME = "c-resin-quantity";

const sanitizeFileNamePart = (value: string | null | undefined): string =>
  value
    ?.trim()
    .replace(/[^a-z0-9-_]+/gi, "-")
    .replace(/^-+|-+$/g, "") ?? "";

/** Builds "<module>-<yyyy-mm-dd>-<calculation>[-<project>].pdf", sanitized, as the lock_calculator report is named. */
export const getConcreteResinReportFileName = (
  moduleName: string | null | undefined,
  calculationName: string | null | undefined,
  projectName: string | null | undefined,
): string => {
  const creationDate = new Date().toISOString().slice(0, 10);
  const parts = [
    sanitizeFileNamePart(moduleName) || DEFAULT_REPORT_MODULE_NAME,
    creationDate,
    sanitizeFileNamePart(calculationName),
    sanitizeFileNamePart(projectName),
  ].filter(Boolean);
  return `${parts.join("-")}.pdf`;
};

/**
 * Renders the PDF report of a Concrete Resin Quantity calculation's saved entries on the server, in
 * body.language, and downloads it. Only for users who can read the calculation, or admins.
 */
export const useConcreteResinReportApi = () => {
  const api = useApi();

  const downloadConcreteResinReport = useCallback(
    (calculationId: string, body: ReportRequestBody, fileName: string): Promise<void> =>
      api.postForFileDownload(getConcreteResinReportUrl(calculationId), body, fileName),
    [api],
  );

  return { downloadConcreteResinReport };
};
